import endOfDay from 'date-fns/endOfDay';
import startOfDay from 'date-fns/startOfDay';
import { useMemo } from 'react';
import { TeacherAssignment } from '../api/AssignmentTeacherApi';

type AssignmentFilter = (assignment: TeacherAssignment) => boolean;

interface UseAssignmentFiltersArgs {
  assignments: TeacherAssignment[];
  selectedMemberIdsPerGroup: Record<string, string[]>;
  dateRange: { from?: Date; to?: Date } | undefined;
  archived?: boolean;
}

export default function useAssignmentFilters({
  assignments,
  selectedMemberIdsPerGroup,
  dateRange,
  archived,
}: UseAssignmentFiltersArgs) {
  const allSelectedMemberIds = useMemo(
    () => new Set(Object.values(selectedMemberIdsPerGroup).flat()),
    [selectedMemberIdsPerGroup],
  );

  const memberFilter = useMemo<AssignmentFilter>(() => {
    if (allSelectedMemberIds.size === 0) return () => true;

    return assignment => {
      const { type, groupId, userIds } = assignment.audience;
      const selectedInGroup = groupId ? selectedMemberIdsPerGroup[groupId] ?? [] : [];

      if (type === 'member') {
        if (!groupId) return userIds?.some(id => allSelectedMemberIds.has(id)) ?? false;

        return userIds?.some(id => selectedInGroup.includes(id)) ?? false;
      }

      if (type === 'group' && groupId) return selectedInGroup.length > 0;

      return false;
    };
  }, [selectedMemberIdsPerGroup, allSelectedMemberIds]);

  const plannedDateRangeFilter = useMemo<AssignmentFilter>(() => {
    if (!dateRange?.from || !dateRange?.to) return () => true;

    const rangeStart = startOfDay(dateRange.from).getTime();
    const rangeEnd = endOfDay(dateRange.to).getTime();

    return assignment => {
      const assignmentStartTime = new Date(assignment.from).getTime();
      const assignmentEndTime = new Date(assignment.to).getTime();

      if (Number.isNaN(assignmentStartTime) || Number.isNaN(assignmentEndTime)) return false;

      return assignmentStartTime <= rangeEnd && assignmentEndTime >= rangeStart;
    };
  }, [dateRange]);

  const archivedDateRangeFilter = useMemo<AssignmentFilter>(() => {
    if (!dateRange?.from || !dateRange?.to) return () => true;

    const rangeStart = startOfDay(dateRange.from).getTime();
    const rangeEnd = endOfDay(dateRange.to).getTime();

    return assignment => {
      const assignmentEndTime = new Date(assignment.to).getTime();

      if (Number.isNaN(assignmentEndTime)) return false;

      return assignmentEndTime >= rangeStart && assignmentEndTime <= rangeEnd;
    };
  }, [dateRange]);

  const filteredAssignments = useMemo(() => {
    return assignments
      .filter(x => (archived ? archivedDateRangeFilter(x) : plannedDateRangeFilter(x)))
      .filter(memberFilter);
  }, [assignments, memberFilter, plannedDateRangeFilter, archivedDateRangeFilter, archived]);

  const selectionCount = useMemo(
    () => Object.values(selectedMemberIdsPerGroup).reduce((total, ids) => total + ids.length, 0),
    [selectedMemberIdsPerGroup],
  );

  return { filteredAssignments, selectionCount };
}
